import { useMemo } from 'react';

import {
    CollapsibleCard,
    ElevatedCard,
    Stack,
} from '@myphx-engineering/phx-design-system';
import { useDisclosure } from '@myphx-engineering/phx-design-system/hooks';

import {
    type CoverageFlags,
    type DigitallyReceivedPrescriberOrderInfoFragment,
    type PriorAuthorizationInfoFragment,
    PriorAuthorizationSource,
} from '../../../graphql/generated/graphql';
import { useFlags } from '../../../hooks/use-flags';
import { usePriorAuthorizationDetails } from '../../../hooks/use-prior-authorization-details';
import { PrescriberOrderDetails } from '../../../routes/orders/details/PrescriberOrderDetails';
import { NotCoveredModal } from '../../common/drug-coverage-alerts/NotCoveredModal';
import { PARequiredAlert } from '../../common/drug-coverage-alerts/PARequiredAlert';
import { PARequiredModal } from '../../common/drug-coverage-alerts/PARequiredModal';
import { MissingBenefitsCard } from '../../prior-authorization/components/MissingBenefitsCard';

import { PatientProductDetailsHeader } from './PatientProductDetailsHeader';

export type PatientProductDetailsProps = {
    patientName?: string;
    productDescription?: string;
    quantity?: number | null;
    prescriberOrder?: DigitallyReceivedPrescriberOrderInfoFragment;
    priorAuth?: PriorAuthorizationInfoFragment;
    coverageFlags?: CoverageFlags;
    togglePARequiredModal?: () => void;
    toggleNotCoveredModal?: () => void;
};

export const PatientProductDetails = ({
    patientName,
    productDescription,
    quantity,
    prescriberOrder,
    priorAuth,
    coverageFlags,
}: PatientProductDetailsProps) => {
    const { nonDrxPriorAuthorizationEnabled } = useFlags();
    const [isPARequiredModalOpen, { toggle: togglePARequiredModal }] =
        useDisclosure(false);
    const [isNotCoveredModalOpen, { toggle: toggleNotCoveredModal }] =
        useDisclosure(false);

    const { isApprovedPriorAuthorization } =
        usePriorAuthorizationDetails(priorAuth);

    // Display PARequiredAlert if there's no coverageFlags, the PA isn't approved yet, and it was initiated from a prescription
    const showPaRequired = useMemo(() => {
        return (
            !coverageFlags &&
            !isApprovedPriorAuthorization &&
            priorAuth?.requestOrigin ===
                PriorAuthorizationSource.PrescriptionInitiated
        );
    }, [priorAuth, coverageFlags, isApprovedPriorAuthorization]);

    const CardHeader = (
        <Stack gap="xs">
            <PatientProductDetailsHeader
                patientName={patientName}
                productDescription={productDescription}
                quantity={quantity}
                prescriberOrder={prescriberOrder}
                coverageFlags={coverageFlags}
                togglePARequiredModal={togglePARequiredModal}
                toggleNotCoveredModal={toggleNotCoveredModal}
            />
            {showPaRequired ? (
                <PARequiredAlert toggle={togglePARequiredModal} />
            ) : null}
        </Stack>
    );

    return (
        <>
            {prescriberOrder ? (
                <CollapsibleCard header={CardHeader} variant="elevated">
                    <PrescriberOrderDetails prescriberOrder={prescriberOrder} />
                </CollapsibleCard>
            ) : (
                <ElevatedCard header={CardHeader} />
            )}
            <PARequiredModal
                opened={isPARequiredModalOpen}
                close={togglePARequiredModal}
            />
            <NotCoveredModal
                opened={isNotCoveredModalOpen}
                close={toggleNotCoveredModal}
            />
            {prescriberOrder ? null : nonDrxPriorAuthorizationEnabled ? (
                <MissingBenefitsCard />
            ) : null}
        </>
    );
};
